export interface StableSlotDraft {
  slotCode: string
  componentId: string
  componentType: string
  required: boolean
  removable: boolean
  reorderable: boolean
  replaceable: boolean
  editableProps: string
  editableStyles: string
}

export interface StructuralChange {
  slotCode: string
  changeType: string
  path: string
  [key: string]: unknown
}

export function buildStableSlots(components: Array<{ id: string; type: string }>): StableSlotDraft[] {
  return components.map(component => ({
    slotCode: component.id.trim(),
    componentId: component.id,
    componentType: component.type,
    required: true,
    removable: false,
    reorderable: false,
    replaceable: false,
    editableProps: '',
    editableStyles: ''
  }))
}

export function hasDuplicateSlotCodes(slots: Array<{ slotCode: string }>) {
  const codes = slots.map(slot => slot.slotCode.trim()).filter(Boolean)
  return new Set(codes).size !== codes.length
}

export function collectTemplateStructuralChanges(
  source: DecorationTemplateVersion | null | undefined,
  target: DecorationTemplateVersion | null | undefined
): StructuralChange[] {
  const sourceSlots = templateSlots(source)
  const targetSlots = templateSlots(target)
  const changes: StructuralChange[] = []
  for (const [slotCode, slot] of sourceSlots) {
    const next = targetSlots.get(slotCode)
    if (!next) {
      changes.push({ slotCode, changeType: 'REMOVED', path: `/slots/${slotCode}` })
    } else if (slot.componentType !== next.componentType) {
      changes.push({ slotCode, changeType: 'REPLACED', path: `/slots/${slotCode}/type` })
    } else if (slot.index !== next.index) {
      changes.push({ slotCode, changeType: 'MOVED', path: `/slots/${slotCode}/index` })
    }
  }
  for (const slotCode of targetSlots.keys()) {
    if (!sourceSlots.has(slotCode)) {
      changes.push({ slotCode, changeType: 'ADDED', path: `/slots/${slotCode}` })
    }
  }
  return changes
}

export function higherTemplateVersions(
  versions: DecorationTemplateVersion[],
  currentVersion: string
) {
  const current = semanticVersion(currentVersion)
  if (!current) return []
  return versions
    .filter(version => version.status === 'LISTED')
    .map(version => ({ version, parts: semanticVersion(version.templateVersion) }))
    .filter((entry): entry is { version: DecorationTemplateVersion; parts: [number, number, number] } =>
      Boolean(entry.parts) && compareVersions(entry.parts!, current) > 0)
    .sort((left, right) => compareVersions(left.parts, right.parts))
    .map(entry => entry.version)
}

export function listedTemplateVersions(versions: DecorationTemplateVersion[]) {
  return versions.filter(version => version.status === 'LISTED')
}

function templateSlots(version: DecorationTemplateVersion | null | undefined) {
  const result = new Map<string, { componentType: string; index: number }>()
  try {
    const policy = JSON.parse(version?.lockPolicyJson || '{}') as unknown
    if (!isRecord(policy) || !Array.isArray(policy.slots)) return result
    for (const [index, value] of policy.slots.entries()) {
      if (!isRecord(value) || typeof value.slotCode !== 'string' || typeof value.componentType !== 'string') continue
      result.set(value.slotCode, { componentType: value.componentType, index })
    }
  } catch {
    return result
  }
  return result
}

export function previewMatchesTarget(
  preview: { targetVersion?: string; applicable?: boolean } | null | undefined,
  targetVersion: string
) {
  return Boolean(preview?.applicable && preview.targetVersion === targetVersion)
}

function semanticVersion(value: string): [number, number, number] | null {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(value)
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null
}

function compareVersions(left: [number, number, number], right: [number, number, number]) {
  for (let index = 0; index < left.length; index += 1) {
    const difference = left[index]! - right[index]!
    if (difference) return difference
  }
  return 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}
import type { DecorationTemplateVersion } from '@xjlv2/api-client'
